import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, FormSection, Input } from '@/components/ui';
import { useChangePasswordMutation } from '../hooks/useMyAccountSecurity';
import { ADMIN_PASSWORD_MIN_LENGTH, isValidAdminPassword } from '../lib/adminPasswordPolicy';

interface FieldErrors {
  current?: string;
  next?: string;
  confirm?: string;
}

export const PasswordChangeForm = () => {
  const { t } = useTranslation('common');
  const mutation = useChangePasswordMutation();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const validate = (): FieldErrors => {
    const e: FieldErrors = {};
    if (current.trim() === '') e.current = t('admin.me.password.errCurrentRequired');
    if (next === '') e.next = t('admin.me.password.errNextRequired');
    // QA-514 — BE 정책(10자 이상 + 영문·숫자·특수문자)과 같게 미리 막는다. 종전 8자 안내로는 서버에서 거부됐다.
    else if (!isValidAdminPassword(next)) e.next = t('admin.me.password.errNextTooShort');
    else if (next === current) e.next = t('admin.me.password.errNextSameAsCurrent');
    if (next !== confirm) e.confirm = t('admin.me.password.errMismatch');
    return e;
  };

  const mapError = (code: string): string => {
    if (code === 'USER_INVALID_CREDENTIALS') {
      return t('admin.me.password.errCurrentInvalid');
    }
    if (code === 'USER_PASSWORD_REUSED') {
      return t('admin.me.password.errReused');
    }
    if (code === 'USER_PASSWORD_POLICY_FAILED') {
      return t('admin.me.password.errWeak');
    }
    return t('admin.me.password.errGeneric');
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSuccess(false);
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    try {
      await mutation.mutateAsync({ currentPassword: current, newPassword: next });
      setSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err: unknown) {
      const code = err instanceof Error ? err.message : 'UNKNOWN';
      if (code === 'USER_INVALID_CREDENTIALS') {
        setErrors((prev) => ({ ...prev, current: t('admin.me.password.errCurrentInvalid') }));
        return;
      }
      setSubmitError(mapError(code));
    }
  };

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(ev) => {
        ev.preventDefault();
        void handleSubmit();
      }}
      data-testid="admin-password-change-form"
    >
      <FormSection
        label={t('admin.me.password.current')}
        density="compact"
        required
        {...(errors.current ? { error: errors.current } : {})}
      >
        <Input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder={t('admin.me.password.currentPlaceholder')}
          autoComplete="current-password"
          aria-invalid={errors.current ? true : undefined}
        />
      </FormSection>

      <FormSection
        label={t('admin.me.password.next')}
        density="compact"
        required
        hint={t('admin.me.password.nextPlaceholder')}
        {...(errors.next ? { error: errors.next } : {})}
      >
        <Input
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder={t('admin.me.password.nextPlaceholder')}
          minLength={ADMIN_PASSWORD_MIN_LENGTH}
          maxLength={100}
          autoComplete="new-password"
          aria-invalid={errors.next ? true : undefined}
        />
      </FormSection>

      <FormSection
        label={t('admin.me.password.confirm')}
        density="compact"
        required
        {...(errors.confirm ? { error: errors.confirm } : {})}
      >
        <Input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={t('admin.me.password.confirmPlaceholder')}
          maxLength={100}
          autoComplete="new-password"
          aria-invalid={errors.confirm ? true : undefined}
        />
      </FormSection>

      {submitError ? (
        <div
          className="rounded-md p-3 text-sm"
          role="alert"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          {submitError}
        </div>
      ) : null}

      {success ? (
        <div
          className="rounded-md p-3 text-sm"
          role="status"
          aria-live="polite"
          style={{ background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }}
        >
          {t('admin.me.password.success')}
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={mutation.isPending}>
          {mutation.isPending
            ? t('admin.me.password.submitting')
            : t('admin.me.password.submit')}
        </Button>
      </div>
    </form>
  );
};
